import { LoadFailed, QueryLoading } from '../components/QueryState';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Button, Panel, PanelHeader } from '@promanage/ui';
import { ROLE_LABELS } from '@promanage/contracts/person-display';
import { nzDate } from '@promanage/core';
import { formatTaxYear, taxYearFor } from '@promanage/core/tax';
import { ReportWorkspace } from '../features/reports/ReportWorkspace';
import { ExportPdfButton } from '../features/reports/ExportPdfButton';
import { OwnerTaxSummaryReport } from '../features/reports/OwnerTaxSummaryReport';
import { TaxSummaryPicker } from '../features/reports/TaxSummaryPicker';
import { parseTaxSummarySearch } from '../features/reports/taxSummarySearch';
import { letterheadLogo } from '../features/branding/letterhead';
import { trpc } from '../lib/trpc';

export const Route = createFileRoute('/reports/tax')({
  // Shape-checked only — see `parseTaxSummarySearch`. A year this agency has no books for is
  // answered by an empty summary, which is a truthful document, not an error.
  validateSearch: parseTaxSummarySearch,
  component: OwnerTaxSummaryPage,
});

/** How many tax years the picker offers. Six covers IRD's seven-year retention with room to spare. */
const YEARS_OFFERED = 6;

/**
 * The owner's income and expense summary, for tax.
 *
 * WHAT IT IS FOR. Once a year, every landlord's accountant asks the agency the same question, and
 * until now the only way to answer it was to open twelve statements and add them up by hand. That
 * is the agency's worst fortnight of the year, it is where the transcription errors are, and every
 * figure needed to answer it has been in the ledger since M1.
 *
 * ONE OWNER AT A TIME, on the same reasoning the statement lands with. The obvious next feature is
 * "run every summary for 2026", and it is deliberately not here: a batch is only useful once these
 * can be DELIVERED, and delivery needs the mailer and the object storage that do not exist yet
 * (ROADMAP M3). A button that produced 140 PDFs into the operator's downloads folder would be a
 * worse answer than the one thing an agency does today, which is produce a document, read it, and
 * send it. What lands first is the document; the schedule attaches to it later and builds the same
 * one, which `buildOwnerTaxSummary` in core is what guarantees.
 *
 * THE SELECTION LIVES IN THE URL, like the statement's. A year-end document is something an
 * operator sends to a colleague to check, reloads after fixing a receipt, and comes back to in
 * October when the accountant finally opens it. Component state survives none of the three.
 *
 * NOTHING IS RECORDED WHEN IT IS PRODUCED, unlike a statement. A statement carries a NUMBER and
 * the number is pinned by the first copy that goes out, so producing one is an act with a
 * consequence. A tax summary has no series and no number — it is a rendering of a closed year, and
 * the same year produced twice is the same document. There is nothing to pin.
 */
function OwnerTaxSummaryPage() {
  const selection = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  /*
   * Which year is "now" — resolved from a NEW ZEALAND day, then stepped back one.
   *
   * Two reasons, and the second is the one that matters. The server runs in UTC, where an Auckland
   * evening is already tomorrow for half the year, so on 31 March at 9pm a UTC date would roll the
   * default into the following tax year — on the one evening this screen is busiest.
   *
   * And the default is the year that has FINISHED, not the one we are in. Nobody files the year
   * they are in; opening on it would show every operator a document headed "Year ended 31 March
   * 2027" that is missing most of its months, every day of the year. The current year is still on
   * the list, for the accountant doing provisional tax, and the report says out loud that it has
   * not ended.
   */
  const today = nzDate(new Date());
  const currentYear = taxYearFor(today);
  const taxYear = selection.year ?? currentYear - 1;
  const refSeq = selection.owner;

  const years = Array.from({ length: YEARS_OFFERED }, (_unused, index) => currentYear - index);

  const owners = useQuery(
    trpc.property.owners.queryOptions(undefined, {
      meta: { errorTitle: 'Could not load owners' },
      staleTime: 60_000,
    }),
  );

  /*
   * Whose agency this is, for the masthead. The same query the account menu runs, so on any
   * session that has opened the menu this is a cache hit. A document handed to somebody's
   * accountant with no agency name on it is a page of figures from nobody in particular.
   */
  const orgs = useQuery(trpc.auth.organisations.queryOptions(undefined, { staleTime: 60_000 }));
  const orgName = orgs.data?.find((org) => org.isCurrent)?.name ?? null;

  const query = useQuery({
    ...trpc.reports.ownerTaxSummary.queryOptions(
      // Only ever called with an owner — the `enabled` below is what makes the fallback safe, and
      // it exists because the options are built before the check.
      { refSeq: refSeq ?? 0, taxYear },
      { meta: { errorTitle: 'We could not produce that summary' }, staleTime: 30_000 },
    ),
    enabled: refSeq !== undefined,
  });

  const summary = query.data;

  return (
    <ReportWorkspace
      title="Income and expenses, for tax"
      subtitle={
        summary === undefined
          ? `${formatTaxYear(taxYear)} — choose an owner`
          : `${summary.label} · ${summary.owner.name}`
      }
      actions={
        <>
          {/*
           * THE PDF IS NOT A NICETY ON THIS ONE. It goes to a third party — the owner's
           * accountant — who is not a user of this system and will file it, so it needs page
           * numbers of its own and a name that says whose year it is. Print stays beside it and
           * costs nothing. See `taxSummaryPdf.ts`.
           */}
          <ExportPdfButton
            label="Download PDF"
            disabled={summary === undefined}
            onExport={async () => {
              if (summary === undefined) return;
              // The renderer, fetched by this click and never by the page view — see
              // DESIGN_SYSTEM.md §8 on why a PDF library is a lazy chunk or a mistake.
              const { exportTaxSummaryPdf } = await import('../features/reports/taxSummaryPdf');
              exportTaxSummaryPdf({
                summary,
                orgName,
                logo: await letterheadLogo(),
                today,
                generatedAt: new Date(),
              });
            }}
          />

          <Button size="sm" onClick={() => window.print()} disabled={summary === undefined}>
            Print
          </Button>
        </>
      }
      controls={
        <Panel className="print:hidden">
          <PanelHeader
            title="Report selection"
            subtitle="One owner, one tax year — 1 April to 31 March, which is twelve of this agency's months exactly."
          />
          <TaxSummaryPicker
            selection={selection}
            taxYear={taxYear}
            years={years}
            owners={owners.data ?? []}
            ownersPending={owners.isPending}
            onChange={(next) => {
              // `replace` so the back button leaves the screen rather than walking back through
              // every owner somebody stepped past on their way down the list.
              void navigate({ search: next, replace: true });
            }}
          />
        </Panel>
      }
    >
      <div className="flex flex-col gap-4">
        {owners.isError ? (
          <LoadFailed retry={() => void owners.refetch()} retrying={owners.isFetching} />
        ) : null}
        {query.isError ? (
          <LoadFailed retry={() => void query.refetch()} retrying={query.isFetching} />
        ) : null}

        {/*
         * WHO IT GOES TO — on the screen, never on the document.
         *
         * It answers the operator's question rather than the owner's. An owner ENTITY does not
         * read a document: "Solomona Holdings Ltd" has a trustee or a director behind it, and an
         * entity with nobody attached is a summary that cannot be sent — which is worth finding
         * out here rather than at year end with a hundred and forty of them to get out.
         */}
        {summary === undefined ? null : (
          <Panel className="flex flex-wrap items-baseline gap-x-2 gap-y-1 px-5 py-3 print:hidden">
            <span className="text-[0.75rem] font-semibold tracking-[0.04em] text-ink-subtle uppercase">
              Goes to
            </span>
            {summary.owner.contacts.length === 0 ? (
              <span className="text-[0.8125rem] text-warn">
                Nobody is attached to {summary.owner.name}. Add a contact on the owner’s record
                before this can be sent.
              </span>
            ) : (
              <span className="text-[0.8125rem]">
                {summary.owner.contacts
                  .map((contact) => `${contact.name} (${ROLE_LABELS[contact.roleType]})`)
                  .join(', ')}
              </span>
            )}
          </Panel>
        )}

        {refSeq === undefined ? (
          <Panel className="px-5 py-10 text-center print:hidden">
            <h2 className="text-[0.9375rem] font-semibold">Choose an owner</h2>
            <p className="mx-auto mt-1 max-w-md text-[0.8125rem] text-ink-muted">
              A tax summary shows one owner what each of their properties earned over a tax year and
              what was charged against it — the twelve monthly statements, added up, in the shape an
              accountant fills a rental schedule from.
            </p>
          </Panel>
        ) : query.isPending ? (
          <QueryLoading />
        ) : summary === undefined ? null : (
          <OwnerTaxSummaryReport
            summary={summary}
            orgName={orgName}
            today={today}
            // Read at render, not pinned: the produced-at line has to be the moment this copy came
            // off the system, and a document left open overnight is one whose figures may have moved.
            generatedAt={new Date()}
          />
        )}
      </div>
    </ReportWorkspace>
  );
}
